import React, { useCallback, useEffect, useState } from "react";
import { ScrollArea } from "@radix-ui/themes";
import "./TableOfContents.scss";
import { Row } from "@/components";
import { useMediaQueryV2 } from "@/hooks/useMediaQuery.ts";
import { ArrowDown } from "@/icons";
import clsx from "clsx";
import { HeadingLevel } from "@/types/headings";

type Heading = {
  id: string;
  text: string;
};

type TableOfContentsProps = {
  title: string;
  headings: Heading[];
  titleTag?: HeadingLevel;
  modeAuthenticated?: boolean;
  isOpen?: boolean;
  onToggle?: () => void;
};

export const TableOfContents = ({
  title = "Indice",
  headings,
  titleTag: TitleTag = "h1",
  modeAuthenticated = false,
  isOpen,
  onToggle = () => {},
}: TableOfContentsProps) => {
  const [activeId, setActiveId] = React.useState("");
  const [scrollProgress, setScrollProgress] = useState(0);
  const isDesktop = useMediaQueryV2("(min-width: 768px)", false);
  const showScrollArea = isDesktop || isOpen;

  // Utilizziamo un ref per bloccare le "intersezioni di passaggio"
  // durante lo smooth scroll avviato da un click.

  const isClickScrolling = React.useRef(false);
  const scrollTimeout = React.useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    const updateScrollProgress = () => {
      const scrollTop = window.scrollY;
      const docHeight =
        document.documentElement.scrollHeight - window.innerHeight;
      const scrollPercent =
        docHeight > 0 ? Math.min((scrollTop / docHeight) * 100, 100) : 0;
      setScrollProgress(scrollPercent);
    };

    window.addEventListener("scroll", updateScrollProgress);
    updateScrollProgress();

    return () => window.removeEventListener("scroll", updateScrollProgress);
  }, []);

  const scrollToActiveItem = useCallback(
    (activeId: string) => {
      if (window.innerWidth < 768) return;

      const activeLink = document.querySelector(
        `a[href="#${activeId}"]`,
      ) as HTMLElement;
      const scrollContainer = document.querySelector(
        ".toc-scroll",
      ) as HTMLElement;

      if (activeLink && scrollContainer) {
        const containerRect = scrollContainer.getBoundingClientRect();
        const linkRect = activeLink.getBoundingClientRect();

        const isVisible =
          linkRect.top >= containerRect.top &&
          linkRect.bottom <= containerRect.bottom;

        if (!isVisible) {
          // Calcoliamo la distanza relativa tra link e top del container a schermo
          const relativeTop = linkRect.top - containerRect.top;
          // Sommiamo lo scroll attuale per ottenere il target esatto "assoluto" del container
          const targetScrollTop = scrollContainer.scrollTop + relativeTop;

          // Centriamo il link all'interno del container
          const centerScrollPos =
            targetScrollTop - containerRect.height / 2 + linkRect.height / 2;

          scrollContainer.scrollTo({
            top: centerScrollPos,
            behavior: "smooth",
          });
        }
      }
    },
    [isDesktop],
  );

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        // Se l'utente sta navigando tramite click, ignoriamo i cambiamenti per evitare salti
        if (isClickScrolling.current) return;

        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveId(entry.target.id);
            scrollToActiveItem(entry.target.id);
          }
        });
      },
      {
        rootMargin: "-90px 0px -70% 0px",
        threshold: 0,
      },
    );

    headings.forEach(({ id }) => {
      const element = document.getElementById(id);
      if (element) observer.observe(element);
    });

    return () => observer.disconnect();
  }, [headings, scrollToActiveItem]);

  return (
    <div
      className={clsx("toc-container", {
        "toc-container-authenticated": modeAuthenticated,
      })}
    >
      <button
        className={"ui-button-accordion-toc"}
        onClick={onToggle}
        type="button"
      >
        <Row className={"ui-header-table-of-contents"}>
          <TitleTag className="text-slate-20 fw-semibold fs-7">
            {title}
          </TitleTag>
          <ArrowDown
            color={"primary"}
            className={clsx("ui-arrow-toc", { "ui-expanded-toc": isOpen })}
          />
        </Row>
      </button>

      <div className="scroll-progress-bar">
        <div
          className="scroll-progress"
          style={{ width: `${scrollProgress}%` }}
        />
      </div>

      {showScrollArea && (
        <ScrollArea
          type="always"
          scrollbars="vertical"
          className="toc-scroll fw-semibold fs-6"
        >
          <div className="toc-list base-col">
            {headings.map((item) => (
              <div key={item.id} className="toc-item">
                <a
                  href={`#${item.id}`}
                  className={`toc-link ${activeId === item.id ? "active text-primary" : "text-slate-28"}`}
                  onClick={(e) => {
                    e.preventDefault();
                    
                    // Attiviamo subito il link desiderato
                    setActiveId(item.id);

                    // Blocchiamo l'Observer per 1 secondo (tempo sufficiente allo smooth scroll nativo)
                    isClickScrolling.current = true;
                    if (scrollTimeout.current) clearTimeout(scrollTimeout.current);
                    scrollTimeout.current = setTimeout(() => {
                      isClickScrolling.current = false;
                    }, 1000);

                    setTimeout(() => {
                      scrollToActiveItem(item.id);
                    }, 100);

                    document
                      .getElementById(item.id)
                      ?.scrollIntoView({ behavior: "smooth" });
                    onToggle();
                  }}
                >
                  {item.text}
                </a>
              </div>
            ))}
          </div>
        </ScrollArea>
      )}
    </div>
  );
};
